import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
// import { useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import { alpha } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";
import makeStyles from "@material-ui/core/styles/makeStyles";
// import CmtImage from "../../../../@coremat/CmtImage";
import IntlMessages from "src/@jumbo/utils/IntlMessages";
// import { AuthMethods } from "src/services/auth";
import ContentLoader from "src/@jumbo/components/ContentLoader";
import Typography from "@material-ui/core/Typography";
// import { CurrentAuthMethod } from "src/@jumbo/constants/AppConstants";
import { resetUserPassword } from "src/api/auth";
import AuthWrapper from "./AuthWrapper";
import { usePasswordRequirements } from "src/helpers/passwordRequirements";


const useStyles = makeStyles((theme) => ({
	authThumb: {
		backgroundColor: alpha(theme.palette.primary.main, 0.12),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 20,
		[theme.breakpoints.up("md")]: {
			width: "50%",
			order: 2,
		},
	},
	authContent: {
		padding: 30,
		[theme.breakpoints.up("md")]: {
			order: 1,
			width: (props) => (props.variant === "default" ? "50%" : "100%"),
		},
		[theme.breakpoints.up("xl")]: {
			padding: 50,
		},
	},
	titleRoot: {
		marginBottom: 14,
		color: theme.palette.text.primary,
	},
	textFieldRoot: {
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: alpha(theme.palette.common.dark, 0.12),
		},
	},
}));
//variant = 'default', 'standard', 'bgColor'
const ResetPassword = ({ variant = "default", wrapperVariant = "default" }) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const location = useLocation();
	
	// Simple approach: detect redirects and reload if needed
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [repeatPwd, setRepeatPwd] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showRepeatPassword, setShowRepeatPassword] = useState(false);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const { tenant } = useSelector(({ auth }) => auth);
	const data = useRef({ hash: null, tenantId: null });
	const hasReloadedRef = useRef(false);
	// const dispatch = useDispatch();
	const classes = useStyles({ variant });

	// Password requirements using helper hook
	const { requirements: currentRequirements, allRequirementsMet } = usePasswordRequirements(password);

	// Detect if this is a redirect and reload if needed - only runs once on mount
	useEffect(() => {
		// Only run once on mount
		if (hasReloadedRef.current) return;
		
		// Check if we have URL parameters
		const urlParams = new URLSearchParams(location.search);
		const emailParam = urlParams.get("email");
		const hashParam = urlParams.get("hash");
		
		// console.log("ResetPassword - Initial load check:", { 
		// 	emailParam, 
		// 	hashParam, 
		// 	hasReloaded: hasReloadedRef.current,
		// 	location: location.pathname + location.search 
		// });
		
		// If we have URL parameters but React Router searchParams are empty, this is likely a redirect
		if (emailParam && hashParam && !searchParams.get("email")) {
			console.log("ResetPassword - Redirect detected, reloading page...");
			hasReloadedRef.current = true;
			// Force a full page reload to ensure URL parameters are properly loaded
			window.location.reload();
			return;
		}
		
		// If we have parameters, set them
		if (emailParam && hashParam) {
			setEmail(emailParam);
			data.current.hash = hashParam;
			data.current.tenantId = urlParams.get("t") ? parseInt(urlParams.get("t")) : null;
			// console.log("ResetPassword - Parameters set successfully:", { 
			// 	email: emailParam, 
			// 	hash: hashParam, 
			// 	tenantId: data.current.tenantId 
			// });
		}
	}, []); // Empty dependency array - only runs once on mount

	// Password visibility toggle functions
	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowRepeatPassword = () => {
		setShowRepeatPassword(!showRepeatPassword);
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		
		// Validate required parameters
		if (!data.current.hash) {
			setError("Invalid or missing reset link. Please request a new password reset.");
			return;
		}
		
		// Validate password requirements
		if (!allRequirementsMet) {
			setError("Please meet all password requirements");
			return;
		}
		
		if (password != repeatPwd) {
			setError("Password didn't match");
			return;
		}
		
		setError(null);
		setLoading(true);
		try {
			await resetUserPassword({ 
				email, 
				hash: data.current.hash, 
				password, 
				tenantId: data.current.tenantId || tenant.id 
			});
			navigate("/login");
		} catch (err) {
			console.error("Reset password failed", err);
			setError(err.message || "Unable to reset password");
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthWrapper variant={wrapperVariant}>
			<Box display="flex" flexDirection="column" alignItems="flex-start" mt={4} style={{ padding: 20 }}>
				<Typography component="h1" variant="h5" align="left" className={classes.titleRoot} style={{ fontWeight: 600, fontSize: 22, color: "#446475", marginBottom: 24, width: '100%' }}>
					Change Password
				</Typography>
				<Typography align="left" style={{ marginBottom: 24, color: "#666", width: '100%' }}>
					{email}
			</Typography>
			{error && <div className="text-red-700 text-center">{error}</div>}
				<form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 550 }}>
					<Box mb={5}>
						<Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
							Enter new password <span style={{ color: 'red' }}>*</span>
						</Typography>
					<TextField
							placeholder="Enter new password"
						fullWidth
						onChange={(event) => setPassword(event.target.value)}
						value={password}
						type={showPassword ? "text" : "password"}
						margin="normal"
						variant="outlined"
						className={classes.textFieldRoot}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={handleClickShowPassword}
										edge="end"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</Box>
				<Box mb={5}>
						<Typography variant="body2" style={{ fontWeight: 500, marginBottom: 1 }}>
							Confirm new password <span style={{ color: 'red' }}>*</span>
						</Typography>
					<TextField
							placeholder="Confirm new password"
						fullWidth
						onChange={(event) => setRepeatPwd(event.target.value)}
						value={repeatPwd}
						type={showRepeatPassword ? "text" : "password"}
						margin="normal"
						variant="outlined"
						className={classes.textFieldRoot}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle confirm password visibility"
										onClick={handleClickShowRepeatPassword}
										edge="end"
									>
										{showRepeatPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</Box>
				
				{/* Password Requirements Checklist */}
				<Box mt={2} mb={3}>
					<Typography variant="body2" style={{ fontWeight: 500, marginBottom: 8 }}>
						Password Requirements:
					</Typography>
					{currentRequirements.map((requirement, index) => (
						<Box key={index} display="flex" alignItems="center" mb={1}>
							{requirement.met ? (
								<CheckCircleIcon style={{ color: '#1976d2', fontSize: 20, marginRight: 8 }} />
							) : (
								<RadioButtonUncheckedIcon style={{ color: '#bdbdbd', fontSize: 20, marginRight: 8 }} />
							)}
							<Typography 
								variant="body2" 
								style={{ 
									color: requirement.met ? '#1976d2' : '#757575',
									fontWeight: requirement.met ? 500 : 400
								}}
							>
								{requirement.label}
							</Typography>
						</Box>
					))}
				</Box>
				
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
					<Box component="p" fontSize={{ xs: 12, sm: 16 }}>
						<NavLink to="/login">Back to login</NavLink>
					</Box>
					<Button type="submit" variant="contained" color="primary" disabled={!allRequirementsMet}>
						<IntlMessages id="appModule.resetPassword" />{" "}
						{loading && <CircularProgress color="inherit" size={"1rem"} className="ml-2" />}
					</Button>
				</Box>

				<Box>
					<Typography>
						Don&apos;t remember your email?
						<Box component="span" ml={2}>
							<Link href="#">
								<IntlMessages id="appModule.contactSupport" />
							</Link>
						</Box>
					</Typography>
				</Box>
			</form>
			<ContentLoader />
			</Box>
		</AuthWrapper>
	);
};

export default ResetPassword;
